/// <reference types="vite/client" />
/**
 * Paddle.js 공용 유틸리티
 *
 * 스크립트 로딩 + 초기화를 한 곳에서 관리하여
 * PricingPage(인라인 오버레이)와 PaddleCheckoutPage(URL 기반 폴백) 모두에서 사용합니다.
 *
 * 콜백은 `openPaddleCheckout` 호출 시마다 갱신되므로
 * 서로 다른 컴포넌트에서 순차적으로 사용해도 안전합니다.
 *
 * Phase 8 — Tenant Gate
 *   테넌트 매니페스트의 `payments.paddle.enabled` 가 primary gate.
 *   false 면 `loadPaddleScript()` / `openPaddleCheckout()` 가 즉시
 *   reject 해 외부 스크립트가 절대 로드되지 않음. B2C 는 manifest
 *   에서 명시적으로 true 를 지정해 기존 동작을 유지.
 */

import { getTenantSnapshot } from '@auw/onprem';
import { resolveTenantPaymentsPaddle } from '@auw/tenant';

type PaddleEnvironment = 'sandbox' | 'production';

interface PaddleEvent {
  name?: string;
  data?: Record<string, unknown>;
}

export interface PaddleApi {
  Initialize: (options: {
    token: string;
    checkout?: { settings?: Record<string, unknown> };
    eventCallback?: (event: PaddleEvent) => void;
  }) => void;
  Checkout: {
    open: (options: { transactionId: string }) => void;
    close?: () => void;
  };
  Environment?: { set: (env: PaddleEnvironment) => void };
}

interface WindowWithPaddle extends Window {
  Paddle?: PaddleApi;
  __askyouraiPaddleInitialized?: boolean;
}

const ENV_CLIENT_TOKEN = String(import.meta.env.VITE_PADDLE_CLIENT_TOKEN || '').trim();
const RAW_ENV = String(import.meta.env.VITE_PADDLE_ENV || 'sandbox').trim().toLowerCase();
const ENV_PADDLE_ENV: PaddleEnvironment =
  RAW_ENV === 'production' || RAW_ENV === 'prod' || RAW_ENV === 'live'
    ? 'production'
    : 'sandbox';

const OVERLAY_CLOSE_DELAY_MS = 250;

/**
 * Resolve the Paddle configuration from the live tenant snapshot with
 * an env-var fallback. Returns `null` when payments are disabled —
 * callers MUST reject/throw in that case.
 */
function resolvePaddleConfig(): {
  readonly clientToken: string;
  readonly environment: PaddleEnvironment;
} | null {
  const tenant = getTenantSnapshot();
  if (tenant) {
    const paddle = resolveTenantPaymentsPaddle(tenant);
    if (!paddle) return null;
    const clientToken =
      (typeof paddle.clientToken === 'string' && paddle.clientToken.trim()) ||
      ENV_CLIENT_TOKEN;
    const environment = paddle.environment ?? ENV_PADDLE_ENV;
    return { clientToken, environment };
  }
  return { clientToken: ENV_CLIENT_TOKEN, environment: ENV_PADDLE_ENV };
}

export function isPaddleEnabled(): boolean {
  const tenant = getTenantSnapshot();
  if (tenant) return resolveTenantPaymentsPaddle(tenant) !== null;
  return ENV_CLIENT_TOKEN.length > 0;
}

function _win(): WindowWithPaddle {
  return window as WindowWithPaddle;
}

let _onCompleted: (() => void) | null = null;
let _onClosed: (() => void) | null = null;

export function loadPaddleScript(): Promise<void> {
  if (!isPaddleEnabled()) {
    return Promise.reject(
      new Error('결제 모듈이 이 테넌트에서 비활성화되어 있습니다.'),
    );
  }
  if (_win().Paddle) return Promise.resolve();

  // 온프렘 번들은 외부 결제 SDK를 동적으로 가져오지 않는다. Samsung
  // manifest에서는 결제가 비활성화되어 이 분기에 도달하지 않으며, 향후
  // 활성화하려면 SDK를 로컬 번들로 먼저 주입해야 한다.
  return Promise.reject(
    new Error('온프렘 번들에 Paddle SDK가 포함되어 있지 않습니다.'),
  );
}

function ensureInitialized(): PaddleApi {
  const paddle = _win().Paddle;
  if (!paddle) throw new Error('Paddle SDK를 불러올 수 없습니다.');
  const config = resolvePaddleConfig();
  if (!config) throw new Error('결제 모듈이 이 테넌트에서 비활성화되어 있습니다.');
  if (!config.clientToken) throw new Error('VITE_PADDLE_CLIENT_TOKEN이 설정되지 않았습니다.');

  if (!_win().__askyouraiPaddleInitialized) {
    if (config.environment === 'sandbox' && paddle.Environment?.set) {
      paddle.Environment.set('sandbox');
    }

    paddle.Initialize({
      token: config.clientToken,
      checkout: { settings: { displayMode: 'overlay', locale: 'ko' } },
      eventCallback: (event: PaddleEvent) => {
        const eventName = String(event?.name || '').trim();

        if (eventName === 'checkout.closed') {
          _onClosed?.();
          return;
        }

        if (eventName === 'checkout.completed') {
          if (typeof paddle.Checkout.close === 'function') {
            window.setTimeout(() => {
              try {
                paddle.Checkout.close?.();
              } catch {
                /* overlay close failure — redirect below recovers UX */
              }
            }, OVERLAY_CLOSE_DELAY_MS);
          }
          _onCompleted?.();
        }
      },
    });
    _win().__askyouraiPaddleInitialized = true;
  }

  return paddle;
}

export interface PaddleCheckoutCallbacks {
  onCompleted?: () => void;
  onClosed?: () => void;
}

export async function openPaddleCheckout(
  transactionId: string,
  callbacks?: PaddleCheckoutCallbacks,
): Promise<void> {
  if (!transactionId) throw new Error('Transaction ID가 없습니다.');

  _onCompleted = callbacks?.onCompleted ?? null;
  _onClosed = callbacks?.onClosed ?? null;

  await loadPaddleScript();
  const paddle = ensureInitialized();
  paddle.Checkout.open({ transactionId });
}
